import React, { useState, useEffect, useCallback } from 'react';
import { useQzTray } from '../context/QzTrayContext';
import Modal from './Modal';
import './WorkstationPrinterModal.css';

export default function WorkstationPrinterModal({ isOpen, onClose }) {
  const qz = useQzTray() || {};
  const {
    connected,
    connecting,
    error: qzError,
    config,
    connect,
    disconnect,
    listPrinters,
    updateConfig,
    printSticker,
  } = qz;

  const [printerList, setPrinterList] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);
  const [testPrintSuccess, setTestPrintSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const refreshPrinters = useCallback(async () => {
    if (!listPrinters) return;
    setLoadingPrinters(true);
    setLocalError('');
    try {
      const list = await listPrinters();
      setPrinterList(Array.isArray(list) ? list : []);
    } catch (err) {
      setLocalError(err?.message || 'Failed to detect printers from QZ Tray.');
      setPrinterList([]);
    } finally {
      setLoadingPrinters(false);
    }
  }, [listPrinters]);

  // When modal opens and QZ is connected, automatically fetch printer list
  useEffect(() => {
    if (isOpen && connected) {
      refreshPrinters();
    }
  }, [isOpen, connected, refreshPrinters]);

  const handleToggleConnect = async () => {
    setLocalError('');
    try {
      if (connected) {
        await disconnect();
      } else {
        await connect();
        await refreshPrinters();
      }
    } catch (err) {
      setLocalError(err?.message || 'Connection failed.');
    }
  };

  const handlePrinterChange = (e) => {
    const selectedPrinter = e.target.value;
    updateConfig({
      printers: { STICKER: selectedPrinter },
      enabled: !!selectedPrinter,
    });
  };

  const handleAutoConnectChange = (e) => {
    updateConfig({
      autoConnect: e.target.checked,
    });
  };

  const handleEnabledChange = (e) => {
    updateConfig({
      enabled: e.target.checked,
    });
  };

  const handleTestPrint = async () => {
    if (!printSticker) return;
    setTestPrinting(true);
    setTestPrintSuccess(false);
    setLocalError('');
    try {
      await printSticker({
        line1: 'PIECE: TEST-101  LOC: A-01-1',
        line2: 'TEST-SKU-PRINT',
        line3: `10.00m  [${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}]`,
        barcodeText: 'TEST-101',
      });
      setTestPrintSuccess(true);
      setTimeout(() => setTestPrintSuccess(false), 3000);
    } catch (err) {
      setLocalError(err?.message || 'Test print failed.');
    } finally {
      setTestPrinting(false);
    }
  };

  if (!isOpen) return null;

  const currentPrinter = config?.printers?.STICKER || '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClassName="ws-printer-modal-content">
      <div className="ws-printer-modal">
        <div className="ws-printer-header">
          <div className="ws-printer-title-group">
            <span className="ws-printer-icon">🖨️</span>
            <div>
              <h3>Workstation Printer Setup</h3>
              <p className="ws-printer-subtitle">
                Configure your local thermal sticker printer (50×25mm) on this PC
              </p>
            </div>
          </div>
        </div>

        {(localError || qzError) && (
          <div className="ws-printer-alert error">
            ⚠️ {localError || qzError}
          </div>
        )}

        {testPrintSuccess && (
          <div className="ws-printer-alert success">
            ✅ Test label sent successfully to your printer!
          </div>
        )}

        {/* QZ Tray Connection Section */}
        <div className="ws-printer-card">
          <div className="ws-printer-card-row">
            <div>
              <span className="ws-printer-label">QZ Tray App Status</span>
              <div className="ws-printer-status-indicator">
                <span className={`ws-status-dot ${connected ? 'connected' : 'disconnected'}`} />
                <strong style={{ color: connected ? '#059669' : '#dc2626' }}>
                  {connecting ? 'Connecting...' : connected ? 'Connected & Ready' : 'Disconnected'}
                </strong>
              </div>
            </div>

            <div className="ws-printer-action-btns">
              <button
                type="button"
                className={`ws-btn ${connected ? 'ws-btn-secondary' : 'ws-btn-primary'}`}
                disabled={connecting}
                onClick={handleToggleConnect}
              >
                {connecting ? '⏳ Connecting...' : connected ? 'Disconnect' : '⚡ Connect QZ Tray'}
              </button>

              {connected && (
                <button
                  type="button"
                  className="ws-btn ws-btn-secondary"
                  disabled={loadingPrinters}
                  onClick={refreshPrinters}
                  title="Detect installed printers"
                >
                  {loadingPrinters ? '🔄 Detecting...' : '🔄 Refresh Printers'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Printer Selection Section */}
        <div className="ws-printer-card">
          <label className="ws-printer-label" htmlFor="ws-sticker-printer-select">
            Sticker / Label Printer (50mm × 25mm)
          </label>
          <select
            id="ws-sticker-printer-select"
            className="ws-printer-select"
            value={currentPrinter}
            onChange={handlePrinterChange}
            disabled={!connected || loadingPrinters}
          >
            <option value="">
              {!connected
                ? '— Connect QZ Tray to choose printer —'
                : printerList.length === 0
                ? '— No printers found (Click Refresh) —'
                : '— Select your thermal sticker printer —'}
            </option>
            {printerList.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {currentPrinter && connected && (
            <div className="ws-printer-active-badge">
              Active Printer: <strong>{currentPrinter}</strong>
            </div>
          )}
        </div>

        {/* Options & Preferences */}
        <div className="ws-printer-card">
          <span className="ws-printer-label">Workstation Preferences</span>
          <div className="ws-printer-checkbox-group">
            <label className="ws-printer-checkbox-label">
              <input
                type="checkbox"
                checked={!!config?.autoConnect}
                onChange={handleAutoConnectChange}
              />
              <span>Auto-connect to QZ Tray whenever this browser opens</span>
            </label>

            <label className="ws-printer-checkbox-label">
              <input
                type="checkbox"
                checked={!!config?.enabled}
                onChange={handleEnabledChange}
              />
              <span>Enable direct printing from this workstation PC</span>
            </label>
          </div>
        </div>

        {/* Test Print Section */}
        <div className="ws-printer-card test-print-card">
          <div>
            <span className="ws-printer-label">Verify Physical Output</span>
            <p className="ws-printer-help-text">
              Sends a sample 50mm × 25mm barcode label directly to the selected printer.
            </p>
          </div>
          <button
            type="button"
            className="ws-btn ws-btn-success"
            disabled={!connected || !currentPrinter || testPrinting}
            onClick={handleTestPrint}
          >
            {testPrinting ? '⏳ Printing...' : '🧪 Send Test Label (50×25mm)'}
          </button>
        </div>

        {/* Quick Instructions & Help */}
        <div className="ws-printer-instructions">
          <h4>📌 Quick Setup Guide:</h4>
          <ol>
            <li>Make sure <strong>QZ Tray</strong> is installed and running in your Windows/Mac taskbar.</li>
            <li>Click <strong>Connect QZ Tray</strong> above and select your thermal label printer.</li>
            <li>Settings are saved per computer. You only need to do this once per workstation!</li>
          </ol>
        </div>

        {/* Modal Footer */}
        <div className="ws-printer-footer">
          <button type="button" className="ws-btn ws-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
